"use client";

// HomepageSvgMap — the homepage world map (SG BETA V2.01 `_lTryDraw` / `_lDraw` / `_lRender`) as a reusable React
// component, first used by the shared mountain selector in place of KitTileMap's 6×4 raster-tile picture.
//
// WHAT IS REUSED, NOT REBUILT:
//   • the scripts: the export's own vendored `d3.min.js`, `topojson-client.min.js` and `world-110m.js`, loaded
//     through `loadDesignScript` (design-script-readiness), so a design route that already started them is
//     adopted, never fetched twice. No new asset, no new dependency.
//   • the geography: `d3.geoNaturalEarth1().rotate([100, 0])`, Antarctica dropped, land #DEE9EC on white with
//     a white 0.7px coastline — `_lDraw` / `_lRender`, via `buildHomeMapRuntime`.
//   • the interaction: `d3.zoom` with the homepage's click distance (14) and a scale extent of 1…220 relative to
//     the whole world; double-click zoom off, as on the homepage.
//   • the pins: `homepage-pin.ts`'s teardrop/glyph tokens, the tier colours, the 0.94 / 1.22 hover scale with the
//     brighter stroke, the `zf / k` counter-scale and the k ≥ 2.2 label rule.
//
// THE PANE: the component fills its nearest positioned ancestor (`position: absolute; inset: 0`). It measures
// that box with a ResizeObserver and does nothing until the box is non-zero, so a hidden pane is never fitted
// to 0×0. At rest every pin is fitted on open and on each ordinary resize; once the visitor pans/zooms (or a
// locate fires) a resize carries the same centre and scale instead. The SVG is drawn at the pane's pixel size
// and zoomed by one group transform, so land and pins stay vector-crisp at every zoom.
//
// FULL SCREEN, IN THE SAME COMPONENT: one expand control turns the SAME mounted element `position: fixed;
// inset: 0` (safe-area padded) — the native Fullscreen API is not used, so it works on iPhone. The selector's
// overlay is itself a fixed, untransformed layer, so a fixed descendant is viewport-relative. Entry and exit
// keep the geographic centre and zoom; exiting without touching the map restores the pane's exact view. The
// control, the × and Escape all exit; Escape is consumed here (capture phase, `preventDefault`) so it never
// reaches the selector's own close handler. Focus is trapped while full and returned to the expand control.

import { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent, ReactNode } from "react";
import { LANDING_MAP_DEPENDENCIES, loadDesignScript } from "@/components/site/design-script-readiness";
import {
  HOME_GLYPH_TRANSFORM, HOME_HOVER_SCALE, HOME_LABEL, HOME_LAND, HOME_LAND_STROKE, HOME_OCEAN, HOME_ORANGE, HOME_PIN_D,
  HOME_SELB, HOME_SELC, brighter, homepageTierColour,
} from "./homepage-pin";
import {
  HOME_CLICK_DISTANCE, HomeMapController, LABEL_Y, WORLD_H, WORLD_W, buildHomeMapRuntime, clampK, clampView, fitWorldPoints,
  isDragGesture, labelOpacity, pinScale, placeHomeLabels, projectPins, relativeZoom, zoomLimits,
} from "./homepage-map-view";
import type { GeoPin, HomeD3Geo, HomeMapRuntime, HomeTopojson, HomeViewApplier, Size, View, WorldPin } from "./homepage-map-view";

// ── THE VENDORED D3 BOUNDARY ─────────────────────────────────────────────────────────────────────────────
// window.d3 is the export's UMD build (public/design/vendor/d3.min.js, v7.9). No @types package is added: these
// are structural types for exactly the zoom / select / transition calls this file makes, nothing more.
type Extent = [[number, number], [number, number]];
/** d3-zoom's ZoomTransform, as read and built here. */
interface D3ZoomTransform {
  readonly x: number;
  readonly y: number;
  readonly k: number;
  translate(x: number, y: number): D3ZoomTransform;
  scale(k: number): D3ZoomTransform;
}
interface D3ZoomEvent { transform: D3ZoomTransform; sourceEvent?: unknown }
/** `zoom.transform(selectionOrTransition, transform)` — what `selection.call(z.transform, t)` invokes. */
type D3TransformFn = (target: D3Selection | D3Transition, t: D3ZoomTransform) => void;
interface D3Transition {
  duration(ms: number): D3Transition;
  ease(fn: (t: number) => number): D3Transition;
  call(fn: D3TransformFn, t: D3ZoomTransform): D3Transition;
}
export interface D3Selection {
  call(fn: (sel: D3Selection) => void): D3Selection;
  call(fn: D3TransformFn, t: D3ZoomTransform): D3Selection;
  on(typenames: string, listener: null): D3Selection;
  interrupt(): D3Selection;
  transition(): D3Transition;
}
/** d3-zoom's ZoomBehavior: callable on a selection, configured by chained setters. */
export interface D3ZoomBehavior {
  (sel: D3Selection): void;
  extent(e: Extent): D3ZoomBehavior;
  scaleExtent(e: [number, number]): D3ZoomBehavior;
  translateExtent(e: Extent): D3ZoomBehavior;
  clickDistance(d: number): D3ZoomBehavior;
  on(typenames: "zoom", listener: (ev: D3ZoomEvent) => void): D3ZoomBehavior;
  transform: D3TransformFn;
}
export interface D3ZoomApi {
  zoom(): D3ZoomBehavior;
  /** typed as `object`: d3.select takes any node; the unit test hands it a plain stand-in node. */
  select(el: object): D3Selection;
  zoomIdentity: D3ZoomTransform;
  easeCubicOut: (t: number) => number;
}

/** The component's d3.zoom: the homepage click distance, every zoom event reported (user = it had a sourceEvent). */
export function bindHomeZoom(d3: D3ZoomApi, onShown: (v: View, user: boolean) => void): D3ZoomBehavior {
  return d3.zoom().clickDistance(HOME_CLICK_DISTANCE).on("zoom", (ev) => {
    onShown({ x: ev.transform.x, y: ev.transform.y, k: ev.transform.k }, Boolean(ev.sourceEvent));
  });
}

/** Configure the zoom for a pane and show `target` through d3's own zoom.transform (which does not constrain:
 *  only gestures do), so a carried fullscreen view is shown exactly as given. */
export function applyHomeView(d3: D3ZoomApi, z: D3ZoomBehavior, sel: D3Selection, target: View, ms: number, b: Size): void {
  z.extent([[0, 0], [b.w, b.h]]).scaleExtent(zoomLimits(b)).translateExtent([[0, 0], [WORLD_W, WORLD_H]]);
  const t = d3.zoomIdentity.translate(target.x, target.y).scale(target.k);
  sel.interrupt();
  if (ms > 0) sel.transition().duration(ms).ease(d3.easeCubicOut).call(z.transform, t);
  else sel.call(z.transform, t);
}

/** The controller's only way out. A fullscreen carry (`exact`) is shown as given; anything else stays inside the
 *  pane's zoom range. With d3 live it goes through d3's own zoom.transform, otherwise to `fallback`. */
export function homeViewApplier(env: {
  d3: () => D3ZoomApi | null;
  zoom: () => D3ZoomBehavior | null;
  node: () => object | null;
  fallback: (v: View) => void;
}): HomeViewApplier {
  return (next, { ms, box: b, exact }) => {
    const target = exact ? next : { ...next, k: clampK(next.k, b) };
    const d3 = env.d3();
    const z = env.zoom();
    const node = env.node();
    if (d3 && z && node) applyHomeView(d3, z, d3.select(node), target, ms, b);
    else env.fallback(exact ? target : clampView(target, b));
  };
}

let runtimeLoad: Promise<HomeMapRuntime> | null = null;
/** Load the three landing-map scripts (adopting tags another writer started) and build the geography once. */
export function loadHomeMapRuntime(): Promise<HomeMapRuntime> {
  if (!runtimeLoad) {
    const load = Promise.all(LANDING_MAP_DEPENDENCIES.map((src) => loadDesignScript(src))).then(() => {
      const w = window as unknown as { d3?: HomeD3Geo; topojson?: HomeTopojson; __sgWorld?: unknown };
      return buildHomeMapRuntime(w.d3, w.topojson, w.__sgWorld);
    });
    // a failed load is not cached: the next open may try again
    load.catch(() => { if (runtimeLoad === load) runtimeLoad = null; });
    runtimeLoad = load;
  }
  return runtimeLoad;
}
const d3Of = (): D3ZoomApi | null => (typeof window === "undefined" ? null : (window as unknown as { d3?: D3ZoomApi }).d3 ?? null);

/** Land never changes after load, so its ~180 paths are not re-diffed on every zoom frame. */
const Land = memo(function Land({ paths }: { paths: string[] }) {
  return <>{paths.map((d, i) => <path key={i} d={d} />)}</>;
});

const tierRank = (p: WorldPin) => (p.status === "bookable" ? 2 : p.status === "info-only" ? 1 : 0);

const PANE_STYLE: CSSProperties = { position: "absolute", inset: 0, overflow: "hidden", background: HOME_OCEAN };
const FULL_STYLE: CSSProperties = { position: "fixed", inset: 0, zIndex: 2400, overflow: "hidden", background: HOME_OCEAN, overscrollBehavior: "contain" };
const PANE_STAGE: CSSProperties = { position: "absolute", inset: 0 };
const FULL_STAGE: CSSProperties = {
  position: "absolute", top: "env(safe-area-inset-top, 0px)", right: "env(safe-area-inset-right, 0px)",
  bottom: "env(safe-area-inset-bottom, 0px)", left: "env(safe-area-inset-left, 0px)",
};
const CONTROL_BOX: CSSProperties = { background: "#fff", border: "1px solid var(--sg-border, #D5E4E8)", borderRadius: 8, boxShadow: "0 2px 8px rgba(31,79,95,0.18)", overflow: "hidden" };
const CONTROL_BTN: CSSProperties = { display: "grid", placeItems: "center", width: 32, height: 32, border: "none", borderBottom: "1px solid #eef2f4", background: "#fff", cursor: "pointer", fontSize: 17, lineHeight: 1, color: "#1f4f5f", padding: 0 };
const safe = (side: "top" | "right" | "bottom" | "left", px: number, full: boolean) => (full ? `calc(${px}px + env(safe-area-inset-${side}, 0px))` : px);

export interface HomepageSvgMapProps {
  /** mountain pins with real lon/lat and the list's own status/href (see `resolveHomepagePins`). */
  pins: GeoPin[];
  /** the list row being hovered, as a pin key — lights the same pin. */
  highlightKey?: string | null;
  /** a locate request: the pin is centred, zoomed in and published as located. */
  focus?: { key: string; nonce: number } | null;
  onHover?: (key: string | null) => void;
  /** shown along the bottom while full screen (the pane's own legend sits outside the map). */
  fullscreenFooter?: ReactNode;
  /** TEST SEAM — an already-built runtime (the unit test builds it from the vendored scripts). */
  runtime?: HomeMapRuntime | null;
  /** TEST SEAM — a pane size to render with before any measurement (server render has no layout). */
  initialBox?: Size | null;
  /** The design menu this map belongs to (its `data-sg-menu` value). The design closes every open menu on a
   *  capturing document pointerdown whose target is outside any [data-sg-menu]; full screen lives in a body
   *  portal, outside the panel, so the map's root carries the owner to stay part of the menu it opened from. */
  menuOwner?: string;
  /** Bump to return to the at-rest fit of every pin (a neutral reopen of the menu that hosts the map). */
  resetNonce?: number;
}

export default function HomepageSvgMap({
  pins,
  highlightKey = null,
  focus = null,
  onHover,
  fullscreenFooter = null,
  runtime: runtimeProp = null,
  initialBox = null,
  menuOwner,
  resetNonce = 0,
}: HomepageSvgMapProps) {
  const shadowId = `hmShadow${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;

  // ── the geography ──────────────────────────────────────────────────────────────────────────────────────
  const [runtime, setRuntime] = useState<HomeMapRuntime | null>(runtimeProp);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (runtime) return;
    let alive = true;
    loadHomeMapRuntime().then((r) => { if (alive) setRuntime(r); }).catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [runtime]);

  // ── the view ───────────────────────────────────────────────────────────────────────────────────────────
  const rootRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [box, setBox] = useState<Size | null>(initialBox);
  /** null until the first view is applied; afterwards the transform d3 (or the fallback) last showed. */
  const [view, setView] = useState<View | null>(null);
  const zoomRef = useRef<D3ZoomBehavior | null>(null);
  const [zoomReady, setZoomReady] = useState(false);
  /** Full screen renders the SAME map into a body portal, so the stage and the SVG are new DOM nodes on every
   *  enter/exit: measurement and the d3 binding re-attach to them (both effects key on `full`) while the
   *  controller — and with it the geographic view — survives in this component's state. */
  const [full, setFull] = useState(false);

  // every decision about when/where the view moves lives in the controller (homepage-map-view.ts)
  const [ctl] = useState(() => {
    const c: HomeMapController = new HomeMapController(homeViewApplier({
      d3: d3Of,
      zoom: () => zoomRef.current,
      node: () => svgRef.current,
      fallback: (v) => { c.shown(v, false); setView(v); },
    }), initialBox);
    return c;
  });

  const worldPins = useMemo(() => (runtime ? projectPins(pins, runtime.project) : []), [pins, runtime]);
  const fit = useMemo(() => (box ? fitWorldPoints(worldPins.map((p) => p.w), box) : null), [worldPins, box]);
  const v = view ?? fit;

  // measure the pane; a zero box (hidden, or not laid out yet) is ignored until it becomes visible
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => {
      const next = { w: el.clientWidth, h: el.clientHeight };
      if (ctl.measure(next)) setBox(next);
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ctl, full]);

  // at rest, every pin is in view: on the first measurement, on an ordinary resize, and when pins arrive
  useEffect(() => {
    ctl.fitChanged(fit);
  }, [fit, zoomReady, ctl]);

  // d3.zoom on the SVG, once it exists; it adopts whatever view is already showing
  const ready = Boolean(runtime && box);
  useEffect(() => {
    const d3 = d3Of();
    const svg = svgRef.current;
    const b = ctl.box;
    if (!ready || !d3?.zoom || !svg || !b) return;
    const z = bindHomeZoom(d3, (nv, user) => {
      ctl.shown(nv, user);
      setView(nv);
    });
    zoomRef.current = z;
    const sel = d3.select(svg);
    sel.call(z).on("dblclick.zoom", null);
    const start = ctl.current();
    if (start) applyHomeView(d3, z, sel, start, 0, b);
    setZoomReady(true);
    return () => {
      sel.interrupt();
      sel.on(".zoom", null);
      zoomRef.current = null;
      setZoomReady(false);
    };
  }, [ready, ctl, full]);

  // a neutral reopen: every pin back in view, at rest again. Declared BEFORE the locate effect, so a reset and a
  // locate landing in the same render reset first and then locate.
  const resetSeen = useRef(resetNonce);
  useEffect(() => {
    if (resetSeen.current === resetNonce) return;
    resetSeen.current = resetNonce;
    ctl.fitAll();
  }, [resetNonce, ctl]);

  // locate: centre the pin, zoomed far enough to separate a cluster (once per request nonce)
  const located = focus ? worldPins.find((p) => p.key === focus.key) : undefined;
  useEffect(() => {
    if (located) ctl.locate(located.w);
    // `focus.nonce` on purpose: pressing locate twice on the same row must pan twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.key, focus?.nonce, Boolean(located), Boolean(box)]);

  const zoomBy = (factor: number) => ctl.zoomBy(factor);
  const fitAll = () => ctl.fitAll();

  // ── full screen ────────────────────────────────────────────────────────────────────────────────────────
  const expandBtn = useRef<HTMLButtonElement>(null);
  const exitBtn = useRef<HTMLButtonElement>(null);
  const returnFocus = useRef(false);
  const enterFull = useCallback(() => {
    ctl.enterFull();
    setFull(true);
  }, [ctl]);
  const exitFull = useCallback(() => {
    ctl.exitFull();
    returnFocus.current = true;
    setFull(false);
  }, [ctl]);

  useEffect(() => {
    // a transition whose resize never came (same size) must not make a later ordinary resize carry
    const t = setTimeout(() => ctl.clearPending(), 1000);
    if (!full) {
      if (returnFocus.current) {
        returnFocus.current = false;
        expandBtn.current?.focus({ preventScroll: true });
      }
      return () => clearTimeout(t);
    }
    const html = document.documentElement;
    const prevOverflow = html.style.overflow;
    html.style.overflow = "hidden";
    // capture on window runs before the selector's document listener; consuming it keeps the selector open
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      exitFull();
    };
    window.addEventListener("keydown", onKey, true);
    const raf = requestAnimationFrame(() => exitBtn.current?.focus({ preventScroll: true }));
    return () => {
      clearTimeout(t);
      html.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey, true);
      cancelAnimationFrame(raf);
    };
  }, [full, exitFull, ctl]);

  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (!full || e.key !== "Tab") return;
    const root = rootRef.current;
    if (!root) return;
    const items = [...root.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')];
    const first = items[0];
    const last = items.at(-1);
    if (!first || !last) return;
    const active = document.activeElement;
    if (e.shiftKey && (active === first || !root.contains(active))) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (active === last || !root.contains(active))) { e.preventDefault(); first.focus(); }
  };

  // ── a pan never becomes a pin pick ─────────────────────────────────────────────────────────────────────
  const press = useRef<{ x: number; y: number } | null>(null);
  const pressing = useRef(new Set<number>());
  const moved = useRef(false);
  const onPointerDownCapture = (e: ReactPointerEvent) => {
    pressing.current.add(e.pointerId);
    if (pressing.current.size === 1) { press.current = { x: e.clientX, y: e.clientY }; moved.current = false; }
    else moved.current = true; // a second finger is a pinch
  };
  const onPointerMoveCapture = (e: ReactPointerEvent) => {
    if (!moved.current && pressing.current.has(e.pointerId) && isDragGesture(press.current, { x: e.clientX, y: e.clientY })) moved.current = true;
  };
  const onPointerEndCapture = (e: ReactPointerEvent) => { pressing.current.delete(e.pointerId); };

  // ── render ─────────────────────────────────────────────────────────────────────────────────────────────
  const state = runtime ? "ready" : failed ? "failed" : "loading";
  const kr = v && box ? relativeZoom(v, box) : 1;
  const zf = pinScale(kr);
  const lit = useMemo(() => new Set([highlightKey, focus?.key].filter((k): k is string => Boolean(k))), [highlightKey, focus?.key]);
  const labelled = v && box ? placeHomeLabels(worldPins, v, box, lit) : new Set<string>();
  // tier order (bookable paints last), then the lit pins above everything — `_lRender`'s raise order
  const ordered = [...worldPins].sort((a, c) => (lit.has(a.key) ? 1 : 0) - (lit.has(c.key) ? 1 : 0) || tierRank(a) - tierRank(c));

  const controls = [
    ["Zoom in", "+", () => zoomBy(1.6)],
    ["Zoom out", "−", () => zoomBy(1 / 1.6)],
    ["Show all mountains", "⤢", fitAll],
  ] as const;

  const map = (
    <div ref={rootRef} data-home-svg-map data-sg-menu={menuOwner} data-map-state={state} data-map-fullscreen={full ? "true" : "false"}
      data-pins={worldPins.length} data-map-zoom={kr.toFixed(2)} data-map-labels={labelled.size}
      role={full ? "dialog" : undefined} aria-modal={full ? true : undefined} aria-label={full ? "Mountain map, full screen" : undefined}
      onKeyDown={onKeyDown} style={full ? FULL_STYLE : PANE_STYLE}>
      <div ref={stageRef} data-home-svg-map-stage style={full ? FULL_STAGE : PANE_STAGE}>
        {runtime && box && v ? (
          <svg ref={svgRef} width={box.w} height={box.h} viewBox={`0 0 ${box.w} ${box.h}`} role="group" aria-label="Mountain map"
            onPointerDownCapture={onPointerDownCapture} onPointerMoveCapture={onPointerMoveCapture}
            onPointerUpCapture={onPointerEndCapture} onPointerCancelCapture={onPointerEndCapture}
            onClickCapture={(e) => {
              if (moved.current) { e.preventDefault(); e.stopPropagation(); }
              moved.current = false;
              press.current = null;
            }}
            style={{ display: "block", touchAction: "none", userSelect: "none", cursor: "grab" }}>
            <defs>
              <filter id={shadowId} x="-60%" y="-60%" width="220%" height="220%">
                <feDropShadow dx={0} dy={1.6} stdDeviation={1.8} floodColor="#111417" floodOpacity={0.3} />
              </filter>
            </defs>
            <g data-home-map-world transform={`translate(${v.x},${v.y}) scale(${v.k})`}>
              <g data-home-map-land fill={HOME_LAND} stroke={HOME_LAND_STROKE} strokeWidth={0.7 / v.k}>
                <Land paths={runtime.land} />
              </g>
              <g data-home-map-pins>
                {ordered.map((p) => {
                  const hot = lit.has(p.key);
                  const base = homepageTierColour(p.status);
                  const c = hot ? brighter(base) : base;
                  const isLocated = focus?.key === p.key;
                  const shown = labelled.has(p.key);
                  const body = (
                    <g transform={`translate(${p.w.x},${p.w.y}) scale(${zf / v.k})`}>
                      {isLocated ? <circle r={19} fill="none" stroke={HOME_ORANGE} strokeWidth={1.2} /> : null}
                      <g style={{ transform: `scale(${hot ? HOME_HOVER_SCALE : 0.94})`, transition: "transform 190ms cubic-bezier(0.2,0,0,1)" }}>
                        <path d={HOME_PIN_D} fill="#fff" stroke={c} strokeWidth={1.5} strokeLinejoin="round" filter={`url(#${shadowId})`} />
                        <g transform={HOME_GLYPH_TRANSFORM}>
                          <path d={HOME_SELB} fill={c} />
                          <path d={HOME_SELC} fill="#fff" />
                        </g>
                      </g>
                      <circle r={24} fill="transparent" />
                      <text y={LABEL_Y} textAnchor="middle" fontFamily={HOME_LABEL.font} fontSize={HOME_LABEL.size} fontWeight={HOME_LABEL.weight}
                        letterSpacing={HOME_LABEL.letterSpacing} fill={HOME_LABEL.fill} stroke={HOME_LABEL.halo} strokeWidth={HOME_LABEL.haloWidth}
                        paintOrder="stroke" opacity={shown ? labelOpacity(kr, hot) : 0} data-home-pin-label={shown ? "shown" : "hidden"}
                        style={{ pointerEvents: "none", transition: "opacity 160ms ease" }}>
                        {p.name.toUpperCase()}
                      </text>
                    </g>
                  );
                  const common = {
                    "data-home-pin": p.key, "data-bookability": p.status ?? "unknown", "data-pin-source": p.source,
                    "data-map-located": isLocated ? "true" : undefined,
                    onMouseEnter: () => onHover?.(p.key), onMouseLeave: () => onHover?.(null),
                    onFocus: () => onHover?.(p.key), onBlur: () => onHover?.(null),
                    style: { cursor: p.href ? "pointer" : "default", outline: "none" },
                  };
                  // a pin with a destination is a real link (the row's own destinationFor href); one without
                  // asserts nothing and goes nowhere
                  return p.href ? (
                    <a key={p.key} href={p.href} aria-label={`Go to ${p.name}`} {...common}>{body}</a>
                  ) : (
                    <g key={p.key} role="img" aria-label={p.name} {...common}>{body}</g>
                  );
                })}
              </g>
            </g>
          </svg>
        ) : null}
        {state !== "ready" ? (
          <div data-home-svg-map-status={state} style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", fontSize: 12, color: "#5b6b72", pointerEvents: "none", padding: 16, textAlign: "center" }}>
            {failed ? "Map is unavailable — pick a mountain from the list." : "Loading map…"}
          </div>
        ) : null}
      </div>

      {full ? (
        <button ref={exitBtn} type="button" data-home-map-control="Close full screen" aria-label="Close full screen" title="Close full screen" onClick={exitFull}
          style={{ ...CONTROL_BOX, ...CONTROL_BTN, position: "absolute", zIndex: 9, top: safe("top", 10, true), right: safe("right", 10, true), borderBottom: "1px solid var(--sg-border, #D5E4E8)", fontSize: 22 }}>
          ×
        </button>
      ) : null}
      <div data-home-map-controls onPointerDown={(e) => e.stopPropagation()}
        style={{ ...CONTROL_BOX, position: "absolute", zIndex: 8, top: safe("top", full ? 52 : 10, full), right: safe("right", 10, full), display: "flex", flexDirection: "column" }}>
        {controls.map(([label, glyph, on]) => (
          <button key={label} type="button" data-home-map-control={label} aria-label={label} title={label} onClick={on} disabled={!v} style={CONTROL_BTN}>
            {glyph}
          </button>
        ))}
        <button ref={expandBtn} type="button" data-home-map-control={full ? "Exit full screen" : "Full screen"} aria-label={full ? "Exit full screen" : "Full screen"}
          title={full ? "Exit full screen" : "Full screen"} aria-pressed={full} onClick={full ? exitFull : enterFull} style={{ ...CONTROL_BTN, borderBottom: "none" }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            {full ? (
              <path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            ) : (
              <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            )}
          </svg>
        </button>
      </div>
      {full && fullscreenFooter ? (
        <div data-home-map-fullscreen-footer style={{ ...CONTROL_BOX, position: "absolute", zIndex: 8, left: safe("left", 12, true), bottom: safe("bottom", 12, true), padding: "8px 12px" }}>
          {fullscreenFooter}
        </div>
      ) : null}
    </div>
  );
  // Full screen leaves the container for document.body: a fixed layer inside a transformed ancestor (the design
  // panel slides in with a transform) would be confined to that ancestor instead of the viewport. The pane keeps
  // an empty placeholder so the layout it sits in does not collapse while the map is away.
  if (!full || typeof document === "undefined") return map;
  return (
    <>
      <div data-home-svg-map-placeholder aria-hidden="true" style={PANE_STYLE} />
      {createPortal(map, document.body)}
    </>
  );
}
